import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { playNotificationChime } from "@/lib/notificationSound";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const seenIds = useRef(null);    // null on very first load — prevents
                                   // a chime from firing on initial fetch
  const soundOn = user?.notification_sound !== false;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const incoming = data.items || [];
      // Detect *new* unread notifications since the last poll. We compare
      // ids, not just unread counts, to avoid double-firing when one item
      // gets marked read locally before the poll finishes.
      if (seenIds.current && soundOn) {
        const fresh = incoming.find(
          (n) => !n.read && !seenIds.current.has(n.id)
        );
        if (fresh) playNotificationChime();
      }
      seenIds.current = new Set(incoming.map((n) => n.id));
      setItems(incoming);
      setUnread(data.unread || 0);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const click = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch (e) {}
    if (n.link) navigate(n.link);
    load();
  };

  const allRead = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (e) {}
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell className="h-5 w-5 text-navy"/>
          {unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-teal text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-serif text-base text-navy">Notifications</div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={allRead} className="h-7 text-xs" data-testid="notification-mark-all">
              <Check className="h-3.5 w-3.5 mr-1"/>Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">No notifications yet</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(n => (
                <li key={n.id}>
                  <button onClick={() => click(n)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${!n.read ? "bg-teal-50/50" : ""}`} data-testid={`notification-${n.id}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-2 h-2 w-2 rounded-full bg-teal flex-shrink-0"/>}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-navy truncate">{n.title}</div>
                        {n.body && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
